import { isEditMode } from 'lib/util/helpers';
import { Module } from 'lib/vendor/terrific/Module';
import { Hook, Sandbox } from 'lib/vendor/terrific/Sandbox';

// Compile regex pattern only once
const regExp = /\$\{\s*([^}\s]+)\s*\}/g;

/**
 * Data Container Terrific Module
 */
export default class DataContainer extends Module {

	private prefix : string;
	private serviceUrl : string;
	private paramName : string;
	private paramValue : string | null;

	private $form : JQuery;
	private $inputs : JQuery;

	constructor($ctx : JQuery, sandbox : Sandbox, id : number, modName : string) {

		super($ctx, sandbox, id, modName);

		this.prefix = $ctx.data('provider-prefix');
		this.serviceUrl = $ctx.data('ajax-url');
		this.paramName = $ctx.data('hash-parameter-uri');
		this.paramValue = $ctx.data('hash-parameter-default');

		this.$form = $ctx.find('form');
		this.$inputs = this.$form.find(':input[data-default-value]')
			.add(this.$form.find('.custom-checkbox, .custom-radio'));

	}

	/**
	 * Hook function to do all of your module stuff.
	 *
	 * @method on
	 * @param  {Hook} callback Callback function
	 * @return void
	 */
	public on(callback : Hook) {

		// Don't replace for author
		if (isEditMode()) { return; }

		const formAction = this.$form.attr('action') || location.href;
		const locationHash = location.hash.substr(1); // Cut '#'
		const locationParams = new URLSearchParams(locationHash);
		// Check that expected hash parameter is present
		if (locationParams.has(this.paramName)) {
			this.paramValue = locationParams.get(this.paramName)!;
			const postData = { hashParameterURI: this.paramValue };
			// Request data for "templating"
			$.post({
				/* Needs to be synced, otherwise ScForm will replace them first */
				async: false,
				data: postData,
				dataType: 'json',
				success: this.onResult.bind(this),
				url: this.serviceUrl,
			});
			// Preserve consumed hashes in form action
			const actionUrl = new URL(formAction, location.href);
			const actionHash = actionUrl.hash.substr(1); // Cut '#'
			const actionParams = new URLSearchParams(actionHash);
			actionParams.set(this.paramName, this.paramValue);
			actionUrl.hash = '#' + actionParams.toString();
			this.$form.attr('action', actionUrl.toString());
		} else {
			// Log warning, since we always expect the hash parameter to be present
			this.log.warn(`Expected hash parameter (${this.paramName}) for DataContainer not provided!`);
		}

		// Append hidden field on the form
		this.$form.append(jQuery('<input>', {
			name: `${this.prefix}_hidden_for_fedp`,
			type: 'hidden', value: this.paramValue,
		}));

		callback();
	}

	private onResult(data : Record<string, unknown>) {
		if (Array.isArray(data) && data.length > 0) {
			// Replace placeholders with data
			this.replacePlaceholders(data[0]);
		} else {
			// Log error, since we always expect some data to be filled in
			this.log.error(`No results for DataContainer (${this.paramValue})`);
		}
	}

	// Replace placeholders in `text` with `data`.
	// Returns the `string` with placeholders replaced.
	// If nothing is replaced, it returns `null` instead.
	private replacePlaceholder(data : any, text : string) : string | null {

		let hasPlaceholder = false;

		const replaced = text.replace(regExp, (matched, placeholder) => {

			hasPlaceholder = true;

			const matches = placeholder.split('.', 2);

			return matches[0] === this.prefix &&
				data[matches[1]] !== undefined ?
					String(data[matches[1]]) : matched;

		});

		if (hasPlaceholder) {
			return replaced;
		}

		return null;

	}

	// Replace placeholders in all text nodes recursively.
	private replaceInTextNodes(data : any) {

		// Return early if option is not enabled
		if (!this.$ctx.data('replace-in-texts')) { return; }

		// Start at the root of the container
		const nodeQueue : HTMLElement[] = [this.$ctx[0]];

		// Process until nothing more to do
		while (nodeQueue.length > 0) {
			// Remove the first item of the queue and process it
			const current : HTMLElement = nodeQueue.shift() as HTMLElement;
			// Clean up all the text nodes under this
			// Element (merge adjacent, remove empty).
			current.normalize();
			// Check all child nodes of the current HTML Element
			for(let child = current.firstChild; child !== null; child = child.nextSibling) {
				// Using a switch for optimal performance
				switch (child.nodeType) {
					case 3: // Node.TEXT_NODE:
						if (child.textContent !== null) {
							const replaced = this.replacePlaceholder(data, child.textContent);
							if (replaced !== null) { child.textContent = replaced; }
						}
						break;
					case 1: // Node.ELEMENT_NODE:
						// Add to queue to be processed later
						nodeQueue.push(child as HTMLElement);
						break;
					default:
						// Nothing to do
						break;
				}
			}
		}

	}

	// Replace placeholders for input elements.
	// Also for defaults stored in data attributes.
	private replaceForInputFields(data : Record<string, unknown>) {

		// Functions differ by element types
		let getter : ($el : JQuery) => string;
		let setter : ($el : JQuery, val : string) => void;

		// Process all fields in the form
		for (const input of this.$inputs) {

			const $input = $(input);

			if($input.is('input')) {
				getter = ($el : JQuery) => String($el.val());
				setter = ($el : JQuery, val : string) => $el.val(val);
			} else if ($input.is('div')) {
				getter = ($el : JQuery) => String($el.data('default-value'));
				setter = ($el : JQuery, val : string) => $el.data('default-value', val);
			} else {
				getter = ($el : JQuery) => String($el.text());
				setter = ($el : JQuery, val : string) => $el.text(val);
			}

			const replaced = this.replacePlaceholder(data, getter($input));

			if (replaced !== null) {
				setter($input, replaced);
			}

		}

	}

	private replacePlaceholders(data : Record<string, unknown>) {
		this.replaceInTextNodes(data);
		this.replaceForInputFields(data);
	}

}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/form/Tc.Module.DataContainer.ts